import { Vector3 } from 'three'
import type { AlignDimensionEntity } from '@/plugins/cad/module/CADModule/model/entity/AlignDimensionEntity'
import {
  ALIGN_DIMENSION_EXTENSION_LINE_EXTEND,
  ALIGN_DIMENSION_EXTENSION_LINE_GAP,
  getAlignDimensionPoints,
} from '@/plugins/cad/module/CADModule/model/entity/AlignDimensionEntity'
import { formatLength } from '@/shared/unitPresentation'
import type { DxfExportContext } from './dxfExportContext'

/**
 * DXF 对齐标注几何与展示数据计算工具，集中处理两端点投影、延伸线、文字位置与显示文本，
 * 供 DxfExporter 在生成 DIMENSION 与匿名块时复用。
 */
export default class DimensionUtils {
  static getAlignedDimensionRenderData(
    entity: AlignDimensionEntity,
    contextOrScale: DxfExportContext | number = 1
  ) {
    const context = typeof contextOrScale === 'number' ? undefined : contextOrScale
    const scale = typeof contextOrScale === 'number' ? contextOrScale : contextOrScale.modelScale
    const p1 = { x: entity.p1.x * scale, y: entity.p1.y * scale }
    const p2 = { x: entity.p2.x * scale, y: entity.p2.y * scale }
    const offsetPoint = { x: entity.offsetPoint.x * scale, y: entity.offsetPoint.y * scale }
    const rawTextPosition = { x: entity.textPosition.x * scale, y: entity.textPosition.y * scale }
    const dimPointData = getAlignDimensionPoints(
      new Vector3(p1.x, p1.y, 0),
      new Vector3(p2.x, p2.y, 0),
      new Vector3(offsetPoint.x, offsetPoint.y, 0),
      ALIGN_DIMENSION_EXTENSION_LINE_GAP * scale,
      ALIGN_DIMENSION_EXTENSION_LINE_EXTEND * scale
    )

    const vx = p2.x - p1.x
    const vy = p2.y - p1.y
    const length = dimPointData.length
    const vLen = length || 1
    const ux = vx / vLen
    const uy = vy / vLen
    const nx = -uy
    const ny = ux
    const d1 = { x: dimPointData.d1.x, y: dimPointData.d1.y }
    const d2 = { x: dimPointData.d2.x, y: dimPointData.d2.y }
    const e1_start = { x: dimPointData.e1_start.x, y: dimPointData.e1_start.y }
    const e1_end = { x: dimPointData.e1_end.x, y: dimPointData.e1_end.y }
    const e2_start = { x: dimPointData.e2_start.x, y: dimPointData.e2_start.y }
    const e2_end = { x: dimPointData.e2_end.x, y: dimPointData.e2_end.y }

    const measurementText = context?.formatsMeterSource
      ? formatLength(length / scale, context.unitPreference).valueText
      : (() => {
          const s = length.toFixed(3)
          return s.replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1')
        })()
    const rawDimensionText = entity.dimensionText ?? ''
    const displayText =
      rawDimensionText.length > 0
        ? rawDimensionText.replaceAll('<>', measurementText)
        : measurementText
    const dimensionText = rawDimensionText.length > 0 ? rawDimensionText : '<>'

    const textHeight = (entity.textHeight ?? 2.5) * scale
    const textGap = textHeight * 0.25
    const arrowSize = Math.max(textHeight, 0.5)
    const textOffsetFromLine = (rawTextPosition.x - d1.x) * nx + (rawTextPosition.y - d1.y) * ny
    const hasUserDefinedTextPosition =
      Math.abs(textOffsetFromLine) > Math.max(1e-6, textHeight * 0.05)
    const textMidPoint = hasUserDefinedTextPosition
      ? rawTextPosition
      : {
          x: rawTextPosition.x + nx * (textGap + textHeight * 0.5),
          y: rawTextPosition.y + ny * (textGap + textHeight * 0.5),
        }

    let textRotationDeg = (Math.atan2(vy, vx) * 180) / Math.PI
    if (textRotationDeg > 90 || textRotationDeg <= -90) {
      textRotationDeg += 180
    }

    return {
      p1,
      p2,
      offsetPoint,
      d1,
      d2,
      e1_start,
      e1_end,
      e2_start,
      e2_end,
      ux,
      uy,
      displayText,
      dimensionText,
      textHeight,
      textGap,
      arrowSize,
      textMidPoint,
      textRotationDeg,
      hasUserDefinedTextPosition,
    }
  }
}
